import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { PlatformProvider } from './contexts/PlatformContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { Toaster } from 'sonner'
import Login from './pages/Login'
import AdminModule from './modules/admin/Admin.container'
import Admin from './pages/Admin'
import UrjaSanyojak from './pages/UrjaSanyojak'
import Dashboard from './pages/Dashboard'
import ExecutiveDashboard from './pages/ExecutiveDashboard'
import GauShala from './pages/GauShala'
import BiogasSangh from './pages/BiogasSangh'
import { SalesModule } from './modules/sales'
import { PurificationModule } from './modules/purification'
import { ExpiringContractsPage } from './features/contracts/expiring'
import UrjaNeta from './pages/UrjaNeta'
import TransporterModule from './modules/transporter'
import RoleBasedRouter from './components/routing/RoleBasedRouter'
import RazorpayDemo from './pages/RazorpayDemo'

// Inventory Batch Pages (US-BATCH-001)
import CreateBatchPage from './pages/inventory/batches/CreateBatchPage'
import BatchListPage from './pages/inventory/batches/BatchListPage'
import BatchDetailPage from './pages/inventory/batches/BatchDetailPage'

// Customer Orders Pages (US-ORD-007)
import CustomerOrdersPage from './pages/commerce/customer-orders'

// Invoice Pages (US-INV-002)
import { InvoiceDetailsPage } from './pages/invoices/InvoiceDetailsPage'
import { InvoicesListPage } from './pages/invoices'

// Overdue Invoice Pages (US-INV-004)
import { OverdueInvoicesPage } from './pages/urjavyapar/OverdueInvoicesPage'
import { CollectionReportPage } from './pages/urjavyapar/CollectionReportPage'

// Landing pages
import LandingLayout from './components/layouts/LandingLayout'
import AdminContentLayout from './components/layouts/AdminContentLayout'
import Home from './pages/landing/Home'
import Benefits from './pages/landing/Benefits'
import About from './pages/landing/About'
import Users from './pages/landing/Users'
import Contact from './pages/landing/Contact'
import SuccessStories from './pages/landing/SuccessStories'
import FAQ from './pages/landing/FAQ'
import NotFound from './pages/landing/NotFound'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  console.log('🔒 [PrivateRoute] Check -', {
    user: user ? `${user.name} (${user.role})` : 'null',
    loading,
    willRedirect: !user && !loading
  })

  if (loading) {
    console.log('⏳ [PrivateRoute] Still loading, showing spinner')
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  if (!user) {
    console.log('🚫 [PrivateRoute] No user found, redirecting to /login')
  }

  return user ? <>{children}</> : <Navigate to="/login" />
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Pages */}
      <Route element={<LandingLayout />}>
        <Route index element={<Home />} />
        <Route path="benefits" element={<Benefits />} />
        <Route path="about" element={<About />} />
        <Route path="user-types" element={<Users />} />
        <Route path="contact" element={<Contact />} />
        <Route path="success-stories" element={<SuccessStories />} />
        <Route path="faq" element={<FAQ />} />
      </Route>

      {/* Public Auth Route */}
      <Route path="/login" element={<Login />} />

      {/* Razorpay Demo Route (Public for testing) */}
      <Route path="/razorpay-demo" element={<RazorpayDemo />} />

      {/* Protected Dashboard Route - Role-based redirect */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <RoleBasedRouter />
        </PrivateRoute>
      } />

      {/* Protected Admin Routes */}
      <Route path="/admin/*" element={
        <PrivateRoute>
          <AdminModule />
        </PrivateRoute>
      } />
      <Route path="/gaushala/*" element={
        <PrivateRoute>
          <GauShala />
        </PrivateRoute>
      } />
      <Route path="/cluster/*" element={
        <PrivateRoute>
          <BiogasSangh />
        </PrivateRoute>
      } />
      <Route path="/sales/*" element={
        <PrivateRoute>
          <SalesModule />
        </PrivateRoute>
      } />
      <Route path="/my-orders" element={
        <PrivateRoute>
          <CustomerOrdersPage />
        </PrivateRoute>
      } />
      <Route path="/contracts/expiring" element={
        <PrivateRoute>
          <ExpiringContractsPage />
        </PrivateRoute>
      } />

      {/* Inventory Batch Routes (US-BATCH-001) */}
      <Route path="/inventory/batches" element={
        <PrivateRoute>
          <BatchListPage />
        </PrivateRoute>
      } />
      <Route path="/inventory/batches/create" element={
        <PrivateRoute>
          <CreateBatchPage />
        </PrivateRoute>
      } />
      <Route path="/inventory/batches/:id" element={
        <PrivateRoute>
          <BatchDetailPage />
        </PrivateRoute>
      } />

      {/* Invoice Routes (US-INV-002) */}
      <Route path="/invoices" element={
        <PrivateRoute>
          <InvoicesListPage />
        </PrivateRoute>
      } />
      <Route path="/invoices/:id/details" element={
        <PrivateRoute>
          <InvoiceDetailsPage />
        </PrivateRoute>
      } />
      <Route path="/invoices/:id" element={
        <PrivateRoute>
          <InvoiceDetailsPage />
        </PrivateRoute>
      } />

      {/* Overdue Invoice Routes (US-INV-004) */}
      <Route path="/urjavyapar/overdue-invoices" element={
        <PrivateRoute>
          <OverdueInvoicesPage />
        </PrivateRoute>
      } />
      <Route path="/urjavyapar/collection-report" element={
        <PrivateRoute>
          <CollectionReportPage />
        </PrivateRoute>
      } />

      <Route path="/purification/*" element={
        <PrivateRoute>
          <PurificationModule />
        </PrivateRoute>
      } />
      <Route path="/urjaneta/*" element={
        <PrivateRoute>
          <UrjaNeta />
        </PrivateRoute>
      } />
      <Route path="/urjasanyojak/*" element={
        <PrivateRoute>
          <UrjaSanyojak />
        </PrivateRoute>
      } />
      <Route path="/admin-dashboard/*" element={
        <PrivateRoute>
          <Admin />
        </PrivateRoute>
      } />
      <Route path="/executive/*" element={
        <PrivateRoute>
          <ExecutiveDashboard />
        </PrivateRoute>
      } />
      <Route path="/transport/*" element={
        <PrivateRoute>
          <TransporterModule />
        </PrivateRoute>
      } />

      {/* Protected Landing Pages for Admin Access */}
      <Route path="/content" element={
        <PrivateRoute>
          <AdminContentLayout />
        </PrivateRoute>
      }>
        <Route path="home" element={<Home />} />
        <Route path="benefits" element={<Benefits />} />
        <Route path="about" element={<About />} />
        <Route path="users" element={<Users />} />
        <Route path="contact" element={<Contact />} />
        <Route path="success-stories" element={<SuccessStories />} />
        <Route path="faq" element={<FAQ />} />
      </Route>

      {/* 404 Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

// Create QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 5, // 5 minutes (previously cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <PlatformProvider>
            <Router>
              <AppRoutes />
              <Toaster position="top-right" richColors closeButton />
            </Router>
          </PlatformProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  )
}

export default App
